import { useCallback } from "react";
import Communicator from "../../Helper/Communicator";
import { ButtonAction, ContentItem } from "../../Helper/CustomWidgetsClient";
import { sanitizeInline } from "../../Helper/sanitize";
import { useRouterStore } from "../../Stores/Router";
import "./ContentRenderer.scss";

const sanitizeInlineHtml = (html: string): string =>
  html ? sanitizeInline(html) : "";

interface ContentRendererProps {
  items: ContentItem[];
}

function ContentRenderer({ items }: ContentRendererProps) {
  const handleAction = useCallback((action: ButtonAction) => {
    if (!action || !action.type) return;
    if (action.type === "open-url") {
      Communicator.openURL(action.url, !!action.newTab);
      return;
    }
    if (action.type === "open-view") {
      useRouterStore.getState().pushPage("custom-view", {
        viewName: action.viewName,
        payload: action.payload ?? null,
      });
      return;
    }
    if (action.type === "custom-action") {
      // The action name and its actionData go to the app's
      // registerCustomAction callback (`data`, on SDKs that pass it).
      if (action.actionName) {
        Communicator.runCustomAction(
          action.actionName,
          undefined,
          action.actionData,
        );
      }
      return;
    }
    if (action.type === "http-request") {
      // Future: proxy through Server to keep auth headers off-iframe.
      // For now, no-op with console hint.
      console.warn(
        "[Gleap] http-request actions are not yet supported on the client.",
      );
    }
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <div className="content-renderer">
      {items.map((item, idx) => {
        if (item.type === "text") {
          return (
            <p className="content-renderer-text" key={idx}>
              {item.text}
            </p>
          );
        }
        if (item.type === "image") {
          return (
            <img
              className="content-renderer-image"
              key={idx}
              src={item.image}
              alt={item.alt ?? ""}
              loading="lazy"
            />
          );
        }
        if (item.type === "button") {
          const style = item.style ?? "primary";
          return (
            <button
              key={idx}
              type="button"
              className={`content-renderer-button content-renderer-button--${style}`}
              onClick={() => handleAction(item.action)}
            >
              {item.label}
            </button>
          );
        }
        if (item.type === "html") {
          return (
            <div
              key={idx}
              className="content-renderer-html"
              dangerouslySetInnerHTML={{
                __html: sanitizeInlineHtml(item.html),
              }}
            />
          );
        }
        if (item.type === "key-value") {
          return (
            <dl className="content-renderer-kv" key={idx}>
              {item.items.map((kv, i) => (
                <div className="content-renderer-kv-row" key={i}>
                  <dt>{kv.key}</dt>
                  <dd>{kv.value}</dd>
                </div>
              ))}
            </dl>
          );
        }
        if (item.type === "divider") {
          return <hr key={idx} className="content-renderer-divider" />;
        }
        return null;
      })}
    </div>
  );
}

export default ContentRenderer;
